<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDeleteProduct } from '../api/deleteProduct';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from '../commands/refreshProductStore';
import { Dialog, Button, DataTable, Column, InputText, ProgressBar, Message } from 'primevue';
import { useTranslations } from 'shared/translations/composables/useTranslations';

const translations = useTranslations();
const t = translations.value.products.deleteMultipleDialog;

const visible = ref(false);
const ids = ref<string[]>([]);
const products = ref<Array<{ id: string; name: string }>>([]);
const confirmation = ref('');
const deleting = ref(false);

const deleteProgress = ref(0);
const deletedIds = ref<Set<string>>(new Set());
const deleteErrorsById = ref<Record<string, string>>({});
const deleteSummary = ref<{ success: number; failed: number } | null>(null);

const abortController = ref<AbortController | null>(null);
const deleteAborted = ref(false)

const hasDeleteAttempt = computed(
  () =>
    deleting.value ||
    deletedIds.value.size > 0 ||
    Object.keys(deleteErrorsById.value).length > 0,
);

defineExpose({
  show,
});

const emit = defineEmits<{
  deleted: [id: string];
}>();

const deleteApi = useDeleteProduct();
const store = useProductStore();
const refreshStore = useRefereshProductStore();

const canDelete = computed(
  () =>
    !deleting.value &&
    confirmation.value === 'DELETE' &&
    deleteSummary.value === null,
);

function show(productIds: string[]) {
  ids.value = productIds;
  confirmation.value = '';
  products.value = [];
  resetDeleteStatus();
  visible.value = true;

  loadProducts();
}

function resetDeleteStatus() {
  deleteProgress.value = 0;
  deletedIds.value = new Set();
  deleteErrorsById.value = {};
  deleteSummary.value = null;
  deleteAborted.value = false;
}

async function loadProducts() {
  await refreshStore.refresh();

  products.value = ids.value.flatMap((id) => {
    const entity = store.getById(id);

    if (!entity) {
      return [];
    }

    return [{
      id: entity.id,
      name: entity.name,
    }];
  });
}

async function onDelete() {
  if (!canDelete.value) {
    return;
  }

  deleting.value = true;
  deleteAborted.value = false;
  resetDeleteStatus();

  const controller = new AbortController();
  abortController.value = controller;

  let success = 0;
  let failed = 0;

  try {
    for (let i = 0; i < ids.value.length; i++) {
      // Stop before starting another delete.
      if (controller.signal.aborted) {
        deleteAborted.value = true
        break;
      }

      const id = ids.value[i];
      if (!id) continue;

      const response = await deleteApi.execute({ id });

      // If Abort was clicked while the request was in flight,
      // don't start another request.
      if (controller.signal.aborted) {
        deleteAborted.value = true
        break;
      }

      if (response.ok) {
        success++;
        deletedIds.value.add(id);
        emit('deleted', id);
      } else {
        failed++;
        deleteErrorsById.value[id] =
          response.details?.title ?? t.deleteFailed;
      }

      deleteProgress.value = Math.round(
        ((i + 1) / ids.value.length) * 100,
      );
    }

    deleteSummary.value = {
      success,
      failed,
    };
  } finally {
    deleting.value = false;
    abortController.value = null;
  }
}

function onAbort() {
  abortController.value?.abort();
}

function onHide() {
  // Don't allow a running delete loop to continue after closing.
  abortController.value?.abort();
  abortController.value = null;

  deleting.value = false;
  confirmation.value = '';
  products.value = [];
  ids.value = [];
  resetDeleteStatus();
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :closable="!deleting" :close-on-escape="!deleting" :header="t.deleteProducts"
    :style="{ width: '50rem', height: '70vh' }" :breakpoints="{ '50rem': '90vw' }" :content-style="{
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }" @hide="onHide">
    <div class="flex flex-col gap-4 flex-1 min-h-0">
      <!-- Description -->
      <div class="flex flex-col gap-1">
        <span>
          {{ t.areYouSure }}
        </span>
        <span>
          {{ t.thisActionCannotBeUndone }}
        </span>
      </div>

      <!-- Delete progress -->
      <ProgressBar v-if="deleting" :value="deleteProgress" :showValue="true" />

      <!-- Delete summary -->
      <Message v-if="deleteSummary" :severity="deleteAborted || deleteSummary.failed > 0 ? 'warn' : 'success'" :closable="false">
        {{ t.deleted }} {{ deleteSummary.success }} {{ t.products }}.

        <template v-if="deleteSummary.failed > 0">
          {{ deleteSummary.failed }} {{ t.summaryFailed }}
          <i class="pi pi-times-circle" style="color: #ef4444" />
          {{ t.hoverIconForDetails }}
        </template>

        <template v-if="deleting === false && abortController === null && deleteProgress < 100">
          {{ t.deleteAborted }}
        </template>
      </Message>

      <!-- Selected products -->
      <DataTable :value="products" scrollable scrollHeight="flex" size="small" stripedRows class="flex-1 min-h-0"
        :emptyMessage="t.noProductsSelected">
        <Column v-if="hasDeleteAttempt" header="Status" style="width: 3.5rem">
          <template #body="{ data }">
            <i v-if="deleteErrorsById[data.id]" class="pi pi-times-circle" style="color: #ef4444"
              :title="deleteErrorsById[data.id]" />
            <i v-else-if="deletedIds.has(data.id)" class="pi pi-check-circle" style="color: #22c55e" />
          </template>
        </Column>
        <Column field="name" :header="translations.generic.product" />
      </DataTable>

      <!-- Confirmation -->
      <div class="flex flex-col gap-2">
        <label for="delete-confirmation" class="font-medium">
          {{ t.type }} <strong>{{ t.DELETE }}</strong> {{ t.toConfirm }}
        </label>

        <InputText id="delete-confirmation" v-model="confirmation" autocomplete="off" :disabled="deleting"
          class="w-full" />
      </div>
    </div>

    <template #footer>
      <Button :label="translations.generic.close" severity="secondary" text :disabled="deleting" @click="visible = false" />

      <Button v-if="!deleting" :label="translations.generic.delete" severity="danger" :disabled="!canDelete" @click="onDelete" />
      <Button v-else="deleting" :label="translations.generic.abort" :loading="true" :disabled="false" severity="danger" icon="pi pi-stop" @click="onAbort" />
    </template>
  </Dialog>
</template>
